import { useState } from 'react';

import { Button } from '../cdl/Button/Button';
import { Callout } from '../cdl/Callout/Callout';
import { DatePicker } from '../cdl/DatePicker/DatePicker';
import { Input } from '../cdl/Input/Input';
import { Text } from '../cdl/Text/Text';
import { useToasts } from '../cdl/Toast/useToasts';
import { CustomerGroupSelect } from '../common/form-elements/CustomerGroupSelect/CustomerGroupSelect';
import { translate } from '../common/helpers/translate.helper';
import { IconInfoCircle } from '../common/icons/cdl/InfoCircle';
import { Box } from '../common/ui/Box';

import { IntegrationOfferSelect } from './components/IntegrationOfferSelect';
import { IntegrationOrderSelect } from './components/IntegrationOrderSelect';
import { IntegrationUserSelect } from './components/IntegrationUserSelect';
import { LinkPurchaseOrderSection } from './components/LinkPurchaseOrderSection';
import { FailedRequisitionsSection } from './FailedRequisitionsSection';
import { useMariAppsCancelRequisition } from './useMariAppsCancelRequisition';
import { useMariAppsImportRequisition } from './useMariAppsImportRequisition';
import { useMariAppsPlacePurchaseOrder } from './useMariAppsPlacePurchaseOrder';
import { useMariAppsUpdatePurchaseOrder } from './useMariAppsUpdatePurchaseOrder';

export const MariAppsPageContent = () => {
    return (
        <div>
            <Callout
                heading={translate('integrationsOverview.calloutMariApps.headline')}
                description={translate('integrationsOverview.calloutMariApps.description')}
                Icon={IconInfoCircle}
            />
            <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap={40} columnGap={40} marginTop={24}>
                <ImportRequisitionSection />
                <CancelRequisitionSection />
                <PlacePurchaseOrderSection />
                <UpdatePurchaseOrderSection />
                <LinkPurchaseOrderSection />
                <Box style={{ gridColumn: '1 / -1' }}>
                    <FailedRequisitionsSection />
                </Box>
            </Box>
        </div>
    );
};

const ImportRequisitionSection = () => {
    const [state, setState] = useState({
        groupId: '',
        requisitionNumber: '',
    });
    const importRequisitionMutation = useMariAppsImportRequisition();
    const { addToast, addErrorToast } = useToasts();

    const handleImport = () => {
        importRequisitionMutation.mutate(
            {
                groupId: state.groupId,
                requisitionNumber: state.requisitionNumber,
            },
            {
                onSuccess: () => {
                    addToast(translate('integrationsOverview.importRequisitionTriggered'));
                },
                onError: (error) => {
                    const errorMessage = error.response?.data?.error || translate('integrationsOverview.unknownError');
                    addErrorToast(errorMessage);
                },
            }
        );
    };

    return (
        <Box display="flex" gap={8} flexDirection="column" alignItems="flex-start">
            <Text variant="title" as="h2">
                {translate('integrationsOverview.importRequisitionTitle')}
            </Text>

            <Box width="100%" display="flex" flexDirection="column" gap={8}>
                <CustomerGroupSelect
                    label={translate('integrationsOverview.groupLabel')}
                    value={state.groupId}
                    onChange={(groupId) => setState({ ...state, groupId: groupId || '' })}
                />

                <Input
                    label={translate('integrationsOverview.requisitionNumberLabel')}
                    value={state.requisitionNumber}
                    onChange={(e) => setState({ ...state, requisitionNumber: e.target.value })}
                />
            </Box>

            <Button
                disabled={!state.groupId || !state.requisitionNumber}
                isLoading={importRequisitionMutation.isPending}
                onClick={handleImport}
            >
                {translate('integrationsOverview.importRequisitionButton')}
            </Button>
        </Box>
    );
};

const PlacePurchaseOrderSection = () => {
    const [state, setState] = useState({
        orderId: '',
        offerId: '',
    });
    const placePurchaseOrderMutation = useMariAppsPlacePurchaseOrder();
    const { addToast, addErrorToast } = useToasts();

    const placePurchaseOrder = () => {
        placePurchaseOrderMutation.mutate(
            {
                orderId: state.orderId,
                offerId: state.offerId,
            },
            {
                onSuccess: () => {
                    addToast(translate('integrationsOverview.placePurchaseOrderTriggered'));
                },
                onError: (error) => {
                    const errorMessage = error.response?.data?.error || translate('integrationsOverview.unknownError');
                    addErrorToast(errorMessage);
                },
            }
        );
    };

    return (
        <Box display="flex" gap={8} flexDirection="column" alignItems="flex-start">
            <Text variant="title" as="h2">
                {translate('integrationsOverview.placePurchaseOrderTitle')}
            </Text>

            <Box width="100%">
                <IntegrationOfferSelect
                    label={translate('integrationsOverview.selectLabel')}
                    value={state.offerId}
                    onChange={(option) =>
                        setState({
                            orderId: option.offer.orderId || '',
                            offerId: option.value,
                        })
                    }
                />
            </Box>

            <Button
                disabled={!state.orderId || !state.offerId}
                isLoading={placePurchaseOrderMutation.isPending}
                onClick={placePurchaseOrder}
            >
                {translate('integrationsOverview.placePurchaseOrderButton')}
            </Button>
        </Box>
    );
};

const UpdatePurchaseOrderSection = () => {
    const [offerId, setOfferId] = useState('');
    const updatePurchaseOrderMutation = useMariAppsUpdatePurchaseOrder();
    const { addToast, addErrorToast } = useToasts();

    const updatePurchaseOrder = () => {
        updatePurchaseOrderMutation.mutate(offerId, {
            onSuccess: () => {
                addToast(translate('integrationsOverview.updatePurchaseOrderTriggered'));
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.error || translate('integrationsOverview.unknownError');
                addErrorToast(errorMessage);
            },
        });
    };

    return (
        <Box display="flex" gap={8} flexDirection="column" alignItems="flex-start">
            <Text variant="title" as="h2">
                {translate('integrationsOverview.updatePurchaseOrderTitle')}
            </Text>

            <Box width="100%">
                <IntegrationOfferSelect
                    label={translate('integrationsOverview.selectLabel')}
                    value={offerId}
                    onChange={(option) => setOfferId(option.value)}
                />
            </Box>

            <Button disabled={!offerId} isLoading={updatePurchaseOrderMutation.isPending} onClick={updatePurchaseOrder}>
                {translate('integrationsOverview.updatePurchaseOrderButton')}
            </Button>
        </Box>
    );
};

const CancelRequisitionSection = () => {
    const [state, setState] = useState({
        enquiryId: '',
        userId: '',
        dateCanceled: undefined as Date | undefined,
    });
    const cancelRequisitionMutation = useMariAppsCancelRequisition();
    const { addToast, addErrorToast } = useToasts();

    const handleCancel = () => {
        if (!state.dateCanceled) return;

        cancelRequisitionMutation.mutate(
            {
                enquiryId: state.enquiryId,
                userId: state.userId,
                dateCanceled: state.dateCanceled,
            },
            {
                onSuccess: () => {
                    addToast(translate('integrationsOverview.cancelRequisitionTriggered'));
                },
                onError: (error) => {
                    const errorMessage = error.response?.data?.error || translate('integrationsOverview.unknownError');
                    addErrorToast(errorMessage);
                },
            }
        );
    };

    return (
        <Box display="flex" gap={8} flexDirection="column" alignItems="flex-start">
            <Text variant="title" as="h2">
                {translate('integrationsOverview.cancelRequisitionTitle')}
            </Text>

            <Box width="100%" display="flex" flexDirection="column" gap={8}>
                <IntegrationOrderSelect
                    label={translate('integrationsOverview.selectLabel')}
                    value={state.enquiryId}
                    onChange={(option) => setState({ ...state, enquiryId: option.order.id })}
                />

                <IntegrationUserSelect
                    label={translate('integrationsOverview.userLabel')}
                    value={state.userId}
                    onChange={(option) => setState({ ...state, userId: option.value })}
                />

                <DatePicker
                    label={translate('integrationsOverview.dateCanceledLabel')}
                    date={state.dateCanceled}
                    onDateChange={(date) => setState({ ...state, dateCanceled: date })}
                />
            </Box>

            <Button
                disabled={!state.enquiryId || !state.userId || !state.dateCanceled}
                isLoading={cancelRequisitionMutation.isPending}
                onClick={handleCancel}
            >
                {translate('integrationsOverview.cancelRequisitionButton')}
            </Button>
        </Box>
    );
};
